<!--

	A generic list of items.

	Typically, the items rendered are dynamically loaded from the "store" and
	"conversionStore" which typically rely on a remote source; the former
	produces a list of identifiers and the latter converts this to the data to
	render, see Config.ts for the expected data for each item.
	Alternatively, the data to display in the list can also be injected more
	statically through the items prop.

	Unless items explicitly override it in their conversion, all items are
	rendered as "template", see again Config.ts for the implemented list items.
	The template also sets the basic configuration of the list, i.e., whether it
	is a swiper, or a bootstrap list, ….

-->
<template>
	<vue-list-loader :loading="showLoader">
		<template #loading>
			<slot name="loading" />
		</template>
		<buffered-items :items="listAdapter">
			<template #default="{ buffered }">
				<component
					:is="effectiveComponent"
					ref="listComponent"
					class="mia-VueList"
					:class="$attrs['class'] || ''"
					:buffered-items="buffered"
					:conversions="conversionAdapter"
					:item-props="itemProps"
					:template="template"
					:leftmost="swiperLeftmost"
					@swiper-ready="$emit('swiper-ready', $event)"
				>
					<template v-for="(_, slot) of $slots" #[slot]="scope">
						<slot :name="slot" v-bind="scope" />
					</template>
				</component>
			</template>
		</buffered-items>
	</vue-list-loader>
</template>
<script lang="ts">
import { type Component as VueComponent } from "vue";
import { Component, Prop, Vue, toNative } from "vue-facing-decorator";
import { type IItems } from "../../Stores/VueList/IItems";
import { PreloadingItems } from "../../Stores/VueList/PreloadingItems";
import { StaticItems } from "../../Stores/VueList/StaticItems";
import { type IConversions } from "../../Stores/VueList/IConversions";
import BufferedItems from "../../Stores/VueList/BufferedItems";
import { useListStore } from "../../Stores/VueList/ListStore";
import { useConversionStore } from "../../Stores/VueList/ConversionStore";
import { DebouncedItems, DebouncedConversions } from "../../Stores/VueList/Debounced";
import { StoreItems } from "../../Stores/VueList/StoreItems";
import { StoreConversions } from "../../Stores/VueList/StoreConversions";
import { CallableConversions } from "../../Stores/VueList/CallableConversions";
import { IndexedConversions } from "../../Stores/VueList/IndexedConversions";
import { StaticConversions } from "../../Stores/VueList/StaticConversions";
import Config from "./Items/Config";
import { isBootstrapTable, isExpandable, isInfiniteLoading, isSwiper } from "./VueListOptions";
import BootstrapTableList from "./BootstrapTableList.vue";
import ExpandableList from "./ExpandableList.vue";
import InfiniteLoadingList from "./InfiniteLoadingList.vue";
import SwiperList from "./SwiperList.vue";
import range from "lodash-es/range";
import { type IVueListOptions } from "./VueListOptions";
import VueListLoader from "@/Control/Vue/Lists/VueList/VueListLoader.vue";
import { isVueListLoading } from "@/Control/Vue/Stores/VueList/ListState";

@Component({
	components: { BufferedItems, VueListLoader },
	inheritAttrs: false,
	emits: ["swiper-ready"],
})
export class VueList<S extends string | number, T> extends Vue {
	@Prop({ required: true }) protected items!: string | S[] | T[];
	@Prop({ required: false, default: null }) protected conversions!: string | ((id: S) => T) | Record<S, T> | null;
	@Prop({ required: false, type: Object, default: {} }) itemProps!: Record<string, any>;
	@Prop({ required: true }) template!: string | IVueListOptions;
	@Prop({ required: false, type: Number, default: 0 }) swiperLeftmost!: number;
	@Prop({ required: false, type: Boolean, default: false }) withLoader!: boolean;

	get showLoader(): boolean {
		if (typeof this.items !== "string" || typeof this.conversions !== "string") {
			return false;
		}

		return this.withLoader && isVueListLoading(this.items, this.conversions);
	}

	private get listOptions(): IVueListOptions {
		if (typeof this.template === "string") {
			const config = Config[this.template];
			if (!config) {
				throw new Error(`ConfigError: No config for template "${this.template}" found in wwwroot/typescript/Control/Vue/Lists/VueList/Items/Config.ts`);
			}
			return config;
		}
		return this.template;
	}

	get effectiveComponent(): VueComponent {
		const options = this.listOptions.backendOptions;

		if (isSwiper(options)) {
			return SwiperList;
		}
		else if (isInfiniteLoading(options)) {
			return InfiniteLoadingList;
		}
		else if (isBootstrapTable(options)) {
			return BootstrapTableList;
		}
		else if (isExpandable(options)) {
			return ExpandableList;
		}
		throw new Error(`unknown list configuration for ${this.template}`);
	}

	get listAdapter(): IItems<S> {
		if (typeof (this.items) == "string") {
			return new PreloadingItems<S>(
				new DebouncedItems<S>(new StoreItems<S>(useListStore(this.items))),
				this.listOptions.pageSize);
		}

		if (Array.isArray(this.items)) {
			if (this.conversions == null) {
				return new StaticItems<S>(range(this.items.length) as S[]);
			}
			return new StaticItems<S>(this.items as S[]);
		}

		throw Error("items must be a list of keys or a list of items or a store identifier");
	}

	get conversionAdapter(): IConversions<S, T> {
		if (typeof (this.conversions) == "string") {
			return new DebouncedConversions<S, T>(new StoreConversions<S, T>(useConversionStore<S, T>(this.conversions)));
		}

		if (this.conversions == null) {
			if (!Array.isArray(this.items)) {
				throw Error("when a VueList receives no conversions, the items must be an array of items");
			}

			return new IndexedConversions<T>(this.items as T[]) as IConversions<S, T>;
		}

		if (typeof this.conversions === "function") {
			return new CallableConversions<S, T>(this.conversions);
		}

		return new StaticConversions<S, T>(this.conversions);
	}
}

export default toNative(VueList);
</script>
